import { useEffect, useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import {
  BotNotFound,
  Button,
  CodeBlock,
  inlineCodeClass,
  inputClass,
  PageSpinner,
} from "./components.tsx";
import { transact, tx, useQuery } from "./db.ts";
import { SigningAuthDocs } from "./signingAuth.tsx";

export const convoEventType = `type ConvoEvent = {
  from: string;
  text: string;
  time: number;
  isOwn?: boolean;
};`;

export const CustomRecorderWebhookConfig = ({ id }: { id: string }) => {
  const { data, error, isLoading } = useQuery({
    bots: { $: { where: { id } } },
  });
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!isLoading && !error) {
      const [bot] = data.bots;
      if (bot?.customRecorderWebhookUrl) {
        setUrl(bot.customRecorderWebhookUrl);
      }
    }
  }, [isLoading, error, data]);

  if (isLoading) return <PageSpinner />;
  if (error) return <div>Error: {error.message}</div>;
  const [bot] = data.bots;
  if (!bot) return <BotNotFound />;

  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm text-text-secondary-light dark:text-text-secondary mb-2">
        Provide a webhook URL to receive conversation events. Every message
        (user, bot, tool calls, tool results) is POSTed as a signed request
        using the same authentication as Custom Tools.
      </div>
      <div className="text-sm text-text-secondary-light dark:text-text-secondary mb-2">
        The request body is{" "}
        <code className={inlineCodeClass}>
          {"SignedToolRequest<ConvoEvent>"}
        </code>{" "}
        where params contains:
      </div>
      <CodeBlock code={convoEventType} lang="typescript" />
      <SigningAuthDocs
        botId={bot.id}
        webhookSecret={bot.webhookSecret}
      />
      <input
        type="url"
        value={url}
        onChange={(e) => setUrl(e.currentTarget.value)}
        className={inputClass}
        placeholder="https://your-server.com/webhook"
        autoComplete="off"
      />
      <div className="flex gap-2">
        <Button
          onClick={() =>
            toast.promise(
              transact(
                tx.bots[bot.id].update({ customRecorderWebhookUrl: url }),
              ),
              {
                loading: "Saving webhook URL...",
                success: "Webhook URL saved!",
                error: (err: Error) =>
                  `Failed to save webhook URL: ${err.message}`,
              },
            )}
          disabled={url === (bot.customRecorderWebhookUrl ?? "")}
        >
          Save
        </Button>
        {bot.customRecorderWebhookUrl && (
          <Button
            onClick={() =>
              toast.promise(
                transact(
                  tx.bots[bot.id].update({ customRecorderWebhookUrl: "" }),
                ),
                {
                  loading: "Removing webhook...",
                  success: () => {
                    setUrl("");
                    return "Webhook removed!";
                  },
                  error: (err: Error) =>
                    `Failed to remove webhook: ${err.message}`,
                },
              )}
            className="bg-danger hover:bg-danger-hover"
          >
            Remove
          </Button>
        )}
      </div>
    </div>
  );
};
